import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Clock, User, Calendar, ArrowRight } from "lucide-react";
import { useArticles } from "../hooks/useArticles";
import { formatDateShort } from "../utils/formatters";
import ImageCarousel from "./ImageCarousel";

const HomePage = () => {
  const { data: articles = [], isLoading, error } = useArticles({ limit: 20 });
  const [sliceLength, setSliceLength] = useState(300);

  useEffect(() => {
    const updateSliceLength = () => {
      const width = window.innerWidth;
      if (width >= 1600) setSliceLength(55);
      else if (width >= 1280) setSliceLength(45);
      else if (width >= 1024) setSliceLength(35);
      else if (width >= 768) setSliceLength(20);
      else setSliceLength(7);
    };
    updateSliceLength();
    window.addEventListener("resize", updateSliceLength);
    return () => window.removeEventListener("resize", updateSliceLength);
  }, []);

  if (error)
    return (
      <div className="min-h-screen flex items-center justify-center text-red-600">
        Failed to load articles: {error.message}
      </div>
    );

  if (isLoading)
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-gray-900 mx-auto mb-3"></div>
          <p className="text-gray-600 text-sm">Loading premium content...</p>
        </div>
      </div>
    );

  const getArticleRoute = (article) => `/article/${article.slug}`;

  // Category filters
  const fashionArticles = articles.filter((a) => a.category === "fashion");
  const technologyArticles = articles.filter((a) => a.category === "technology");
  const travelArticles = articles.filter((a) => a.category === "travel");
  const luxuryArticles = articles.filter(a => a.category === 'luxury');
  const foodArticles = articles.filter((a) => a.category === "food");
  const peopleArticles = articles.filter(a => a.category === 'people');

  const heroArticle = luxuryArticles.find(a => a.slug?.includes('sunseeker')) || luxuryArticles[0] || articles[1];

  const mixedArticles = [
    ...fashionArticles.slice(0, 2),
    ...technologyArticles.slice(0, 2),
    ...travelArticles.slice(0, 2),
  ];

  const featuredArticles = articles.slice(0, 3); // show 3 spotlight articles

  const featured_Articles = articles.slice(0, 9); // show 3 spotlight articles

  return (
    <div
      className="min-h-screen bg-gray-50 px-4 md:px-8 py-6"
      style={{
        fontFamily:
          'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, "Noto Sans", sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
      }}
    >
      <div className="bg-white grid grid-cols-1 lg:grid-cols-3 gap-6 items-start px-4 sm:px-6 lg:px-12">
        {/* LEFT COLUMN — smaller list with round images */}

        <div className="lg:col-span-2 flex flex-col">
          {/* Carousel section */}
          <div className="bg-white shadow rounded-2xl overflow-hidden w-full">
            <ImageCarousel />
          </div>

          {/* Featured Articles below carousel */}
          <div className="mt-6">
            <h2 className="text-lg font-semibold text-gray-800 mb-3">
              Spotlight
            </h2>
            <div className="grid sm:grid-cols-3 gap-4">
              {featuredArticles.map((article, i) => (
                <motion.div
                  key={i}
                  whileHover={{ scale: 1.02 }}
                  className="bg-white rounded-xl shadow-sm hover:shadow-md overflow-hidden transition"
                >
                  <Link to={getArticleRoute(article)}>
                    {article.hero_image && (
                      <img
                        src={article.hero_image}
                        alt={article.title}
                        className="h-48 w-full object-cover"
                      />
                    )}
                    <div className="p-4">
                      <h3 className="text-base font-semibold text-gray-800 line-clamp-2">
                        {article.title}
                      </h3>
                      <p className="text-xs text-gray-500 mt-2">
                        <User className="inline w-3 h-3 mr-1" />
                        {article.author || "Editor"} •{" "}
                        <Clock className="inline w-3 h-3 mr-1" />
                        {formatDateShort(article.created_at)}
                      </p>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>
        </div>

        {/* RIGHT COLUMN — carousel at top, featured articles below */}
        <div className="lg:col-span-1 flex flex-col bg-white shadow rounded-2xl overflow-hidden">
          <h2 className="text-lg font-semibold text-gray-800 border-b px-4 py-3">
            Latest from Categories
          </h2>
          <div className="overflow-y-auto p-4 space-y-4 flex-1">
            {mixedArticles.map((article, i) => (
              <motion.div
                key={i}
                whileHover={{ scale: 1.02 }}
                className="bg-gray-50 hover:bg-gray-100 rounded-xl shadow-sm transition"
              >
                <Link
                  to={getArticleRoute(article)}
                  className="flex gap-3 p-3 items-center"
                >
                  {article.hero_image && (
                    <img
                      src={article.hero_image}
                      alt={article.title}
                      className="w-20 h-20 object-cover rounded-full border border-gray-200"
                    />
                  )}
                  <div className="flex flex-col justify-between">
                  <h3
                    className="text-sm font-semibold text-gray-800 line-clamp-2"
                    style={{ fontFamily: 'Roboto, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, "Noto Sans", sans-serif' }}
                  >
                    {article.title}
                  </h3>
                  {article.summary && (
                    <p
                      className="text-xs text-gray-600 line-clamp-2 mt-1"
                      style={{ fontFamily: 'Roboto, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, "Noto Sans", sans-serif' }}
                    >
                      {article.summary}
                    </p>
                  )}

                    <p className="text-xs text-gray-500 mt-1">
                      <Calendar className="inline w-3 h-3 mr-1" />
                      {formatDateShort(article.created_at)}
                    </p>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>

        
        
      </div>

      {/* FASHION */}
      {fashionArticles.length > 0 && (
        <section className="bg-white py-6">
          <div className="w-full px-4 sm:px-6 lg:px-12">
            <div className="mb-6 flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-serif font-bold mb-2">Fashion</h2>
                <p className="text-gray-900 text-base">Style, trends, and designer insights</p>
              </div>
            </div>

            {/* Horizontal scroll container with auto scroll */}
            <div 
              className="flex gap-4 overflow-x-hidden relative" 
              style={{ whiteSpace: 'nowrap' }}
            >
              <div
                className="flex gap-4 animate-scroll"
                style={{ display: 'inline-flex' }}
              >
                {fashionArticles.concat(fashionArticles).slice(0, 24).map(article => (
                  <Link 
                    key={article.id + Math.random()} 
                    to={getArticleRoute(article)} 
                    className="w-56 flex-shrink-0 relative group"
                  >
                    <img 
                      src={article.hero_image} 
                      alt={article.title} 
                      className="w-full h-[170px] object-cover rounded-md group-hover:opacity-90 transition"
                      onError={(e) => {
                        e.target.src = 'https://images.unsplash.com/photo-1503342217505-b0a15ec3261c?w=400&h=300&fit=crop&auto=format&q=80';
                      }}
                    />
                    <div className="absolute bottom-2 left-2 right-2 bg-black bg-opacity-50 text-white px-2 py-1.5 rounded text-xs">
                      <h4 className="font-semibold truncate">{article.title}</h4>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </div>

          {/* Tailwind custom animation */}
          <style jsx>{`
            .animate-scroll {
              animation: scroll-left 50s linear infinite;
            }
            @keyframes scroll-left {
              0% { transform: translateX(0); }
              100% { transform: translateX(-50%); }
            }
          `}</style>
        </section>
      )}
      
      {/* TECHNOLOGY SECTION */}
      {technologyArticles.length > 0 && (
        <section className="bg-gray-100 py-6">
          <div className="w-full px-4 sm:px-6 lg:px-12">
            {/* Section Header */}
            <div className="mb-8 text-center lg:text-left">
              <h2 className="text-2xl font-serif font-bold mb-2 text-gray-900">
                Technology
              </h2>
              <p className="text-gray-700 text-base">
                Latest trends, gadgets, and tech insights
              </p>
            </div>

            {/* TOP ROW */}
            <div className="grid lg:grid-cols-3 gap-6">
              {/* MAIN ARTICLE */}
              {technologyArticles[0] && (
                <Link
                  to={`/article/${technologyArticles[0].slug}`}
                  className="group relative rounded-lg overflow-hidden h-64 sm:h-72 lg:h-[23rem] lg:col-span-2"
                >
                  <img
                    src={technologyArticles[0].hero_image}
                    alt={technologyArticles[0].title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent flex items-end p-6">
                    <div>
                      <span className="text-sm uppercase tracking-wide text-blue-400 font-semibold">
                        {technologyArticles[0].category}
                      </span>
                      <h3 className="text-xl lg:text-2xl font-bold text-white mt-2 group-hover:text-gray-200 transition">
                        {technologyArticles[0].title}
                      </h3>
                      <p className="text-gray-300 mt-1 line-clamp-3">
                        {technologyArticles[0].summary}
                      </p>
                    </div>
                  </div>
                </Link>
              )}

              {/* TWO ARTICLES RIGHT (SIDE BY SIDE) */}
              <div className="grid grid-cols-2 gap-4 lg:flex lg:flex-col lg:gap-6">
                {technologyArticles.slice(1, 3).map(article => (
                  <Link
                    key={article.id}
                    to={`/article/${article.slug}`}
                    className="group relative rounded-lg overflow-hidden h-32 sm:h-36 lg:h-44"
                  >
                    <img
                      src={article.hero_image}
                      alt={article.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                    <div className="absolute inset-0 bg-black bg-opacity-40 flex items-end p-3">
                      <h4 className="text-white font-semibold text-sm line-clamp-2 group-hover:text-gray-200 transition">
                        {article.title}
                      </h4>
                    </div>
                  </Link>
                ))}
              </div>
            </div>

            {/* BOTTOM ROW: HIDDEN ON MOBILE */}
            <div className="hidden sm:grid grid-cols-5 gap-4 mt-6">
              {technologyArticles.slice(3, 8).map(article => (
                <Link
                  key={article.id}
                  to={`/article/${article.slug}`}
                  className="group relative w-full aspect-square rounded-lg overflow-hidden"
                >
                  <img
                    src={article.hero_image}
                    alt={article.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute inset-0 bg-black bg-opacity-40 flex items-end p-2">
                    <h4 className="text-white font-semibold text-xs line-clamp-2 group-hover:text-gray-200 transition">
                      {article.title}
                    </h4>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* HERO SECTION */}
      {heroArticle && (
        <section className="bg-white relative overflow-hidden py-4 px-4 mb-2 mx-2 rounded-lg lg:px-12 lg:mx-12">
        {/* Full-section blurred background */}
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `url(${heroArticle.hero_image})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            filter: 'blur(20px)',
            transform: 'scale(1.1)',
            zIndex: 0,
          }}
        ></div>
      
        <div className="absolute inset-0 bg-black/30 z-0"></div>
      
        <div className="flex flex-col lg:grid lg:grid-cols-2 gap-0 items-stretch min-h-[40vh] lg:min-h-[62vh] relative z-10">
          {/* IMAGE SIDE */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            className="relative h-60 lg:h-[60vh]"
          >
            <img
              src={heroArticle.hero_image}
              alt={heroArticle.title}
              className="w-full h-full object-cover rounded-md"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-black/10 to-transparent"></div>
            <div className="absolute bottom-4 left-4">
              <span className="text-white bg-black/40 px-2 lg:px-3 py-1 rounded-full text-xs uppercase tracking-wide">
                {heroArticle.category}
              </span>
            </div>
          </motion.div>
      
          {/* CONTENT SIDE */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            className="flex flex-col justify-start px-4 pt-4 pb-2 lg:px-16 lg:pt-6 lg:pb-4"
          >
            <div className="max-w-lg text-white">
              <h2 className="text-lg lg:text-5xl font-serif font-bold mb-2 lg:mb-4">
                {heroArticle.title}
              </h2>
              <p className="text-white text-sm lg:text-base mb-2 lg:mb-6 line-clamp-3 lg:line-clamp-4">
                {heroArticle.summary}
              </p>
              <div
                className="text-white text-sm lg:text-base mb-2 lg:mb-6 prose max-w-none"
                dangerouslySetInnerHTML={{
                  __html:
                    heroArticle.body
                      ?.split(' ')
                      .slice(0, sliceLength)
                      .join(' ') + '...',
                }}
              />
              <div className="flex flex-wrap items-center gap-2 lg:gap-5 text-xs lg:text-sm text-white mb-2 lg:mb-6">
                {/* author, date, reading time */}
                <div className="flex items-center gap-1 lg:gap-2">
                  <User size={16} />
                  <span>{heroArticle.author_name}</span>
                </div>
                <div className="flex items-center gap-1 lg:gap-2">
                  <Calendar size={16} />
                  <span>{formatDateShort(heroArticle.published_at)}</span>
                </div>
                <div className="flex items-center gap-1 lg:gap-2">
                  <Clock size={16} />
                  <span>{heroArticle.reading_time || '5 min read'}</span>
                </div>
              </div>
              <Link
                to={`/article/${heroArticle.slug}`}
                className="inline-flex items-center gap-1 lg:gap-2 bg-white/20 text-white px-3 py-1.5 text-xs
                lg:px-6 lg:py-3 lg:text-base  rounded-full font-medium hover:bg-white/30 transition"
              >
                Read Full Story
                <ArrowRight size={16} className="lg:size-[18px]" />
              </Link>

            </div>
          </motion.div>
        </div>
      </section>
      
        
      )}

     {/* TRAVEL SECTION */}
      {travelArticles.length > 0 && (
        <section className="bg-green-50 py-2 px-4 sm:px-6 lg:px-12">
          <div className="mb-2">
            <span className="text-xs text-gray-500 uppercase tracking-wide">Travel Stories</span>
            <h2 className="text-2xl md:text-3xl font-bold text-gray-800 mt-1">
              Explore the World Through These Stories
            </h2>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            {/* Left Column - Main Article */}
            {travelArticles[0] && (
              <Link
                to={`/article/${travelArticles[0].slug}`}
                className="group relative rounded-lg overflow-hidden hidden lg:block col-span-8"
                style={{ height: '90%' }} // matches the sub-articles list
              >
                <img
                  src={travelArticles[0].hero_image}
                  alt={travelArticles[0].title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                />
                <div className="absolute inset-0 bg-black/20 flex items-end p-6">
                  <div>
                    <span className="text-sm uppercase tracking-wide text-green-800 font-semibold">
                      {travelArticles[0].category}
                    </span>
                    <h3 className="text-black text-2xl font-bold mt-1 group-hover:text-gray-800 transition">
                      {travelArticles[0].title}
                    </h3>
                    <p className="text-black text-sm line-clamp-3 mt-1">
                      {travelArticles[0].summary}
                    </p>
                  </div>
                </div>
              </Link>
            )}

            {/* Right Column - List of Sub Articles */}
            <div className="flex flex-col gap-4 col-span-12 lg:col-span-4">
              {travelArticles.slice(1, 6).map((article) => (
                <Link
                  key={article.id}
                  to={`/article/${article.slug}`}
                  className="group flex gap-4 items-start rounded-lg overflow-hidden bg-white p-2 hover:bg-gray-100 transition"
                >
                  {/* Sub-article image */}
                  <img
                    src={article.hero_image}
                    alt={article.title}
                    className="w-20 h-20 object-cover rounded-md flex-shrink-0"
                  />
                  {/* Sub-article text */}
                  <div className="flex flex-col justify-start">
                    <h4 className="text-black font-semibold text-sm line-clamp-2 group-hover:text-gray-800">
                      {article.title}
                    </h4>
                    <p className="text-gray-700 text-xs line-clamp-3 mt-1">
                      {article.summary}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* PEOPLE SECTION */}
      {peopleArticles.length > 0 && (
        <section className="bg-blue-50 py-8 px-4 sm:px-6 lg:px-12">
          <div className="mb-4">
          <span className="text-xs text-gray-500 uppercase tracking-wide">Inspiring People</span>
          <h2 className="text-2xl md:text-3xl font-bold text-gray-800 mt-1">
            Stories about Inspiring People
          </h2>
        </div>


          {/* Masonry style grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {peopleArticles.slice(0, 8).map(article => (
              <Link
                key={article.id}
                to={`/article/${article.slug}`}
                className="group relative rounded-lg overflow-hidden h-64"
              >
                <img
                  src={article.hero_image}
                  alt={article.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent flex items-end p-3">
                  <h4 className="text-white font-semibold text-sm line-clamp-2">{article.title}</h4>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* FOOD SECTION */}
      {foodArticles.length > 0 && (
        <section className="bg-yellow-50 py-8 px-4 sm:px-6 lg:px-12">
          <div className="mb-6">
            <span className="text-xs text-gray-500 uppercase tracking-wide">Food Stories</span>
            <h2 className="text-2xl md:text-3xl font-bold text-gray-800 mt-1">
              Delicious Recipes and Food Experiences
            </h2>
          </div>


          {/* Horizontal scroll cards */}
          <div className="flex gap-4 overflow-x-auto hide-scrollbar pb-3">
            {foodArticles.slice(0, 12).map(article => (
              <Link
                key={article.id}
                to={`/article/${article.slug}`}
                className="w-56 flex-shrink-0 relative group"
              >
                <img
                  src={article.hero_image}
                  alt={article.title}
                  className="w-full h-40 object-cover rounded-md group-hover:opacity-90 transition"
                />
                <div className="absolute bottom-2 left-2 right-2 bg-black bg-opacity-50 text-white px-2 py-1.5 rounded text-xs">
                  <h4 className="font-semibold truncate">{article.title}</h4>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* FEATURED ARTICLES SECTION */}
      <section className="bg-[#021740] py-10 mx-4 lg:mx-12 rounded-lg">
        <div className="w-full px-4 sm:px-6 lg:px-12">
          <div className="mb-8">
            <h2 className="text-3xl font-serif font-bold text-white mb-2">
              Featured Stories
            </h2>
            <p className="text-gray-300">
              Exclusive content and premium experiences
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* LEFT COLUMN (main + 2 below) */}
            <div className="md:col-span-2 space-y-6">
              
              {/* Main Article */}
              {featured_Articles[0] && (
                <Link to={getArticleRoute(featured_Articles[0])} className="block group">
                  <div className="relative rounded-lg overflow-hidden h-64 sm:h-72 lg:h-80">
                    <img
                      src={featured_Articles[0].hero_image}
                      alt={featured_Articles[0].title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                    <div className="absolute inset-0 bg-black bg-opacity-40 flex items-end p-6">
                      <div>
                        <span className="text-sm uppercase tracking-wide text-pink-400 font-semibold">
                          {featured_Articles[0].category}
                        </span>
                        <h3 className="text-xl lg:text-2xl font-bold text-white mb-2 group-hover:text-gray-200 transition">
                          {featured_Articles[0].title}
                        </h3>
                        <p className="text-gray-300 text-sm line-clamp-2">
                          {featured_Articles[0].summary}
                        </p>
                      </div>
                    </div>
                  </div>
                </Link>
              )}

              {/* Two bigger articles below main */}
              <div className="grid grid-cols-2 sm:grid-cols-2 gap-4 sm:gap-6">
                {featured_Articles.slice(1, 3).map(article => (
                  <Link key={article.id} to={getArticleRoute(article)} className="block group">
                    <div className="relative rounded-lg overflow-hidden h-36 sm:h-44 lg:h-52">
                      <img
                        src={article.hero_image}
                        alt={article.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent flex items-end p-4">
                        <h4 className="text-sm sm:text-base font-semibold text-white line-clamp-2 group-hover:text-gray-200 transition">
                          {article.title}
                        </h4>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>

            {/* RIGHT COLUMN (unchanged) */}
            <div className="flex flex-col h-full space-y-6">
              {featured_Articles.slice(3, 6).map(article => (
                <Link
                  key={article.id}
                  to={getArticleRoute(article)}
                  className="group flex-1 flex gap-4"
                >
                  <img
                    src={article.hero_image}
                    alt={article.title}
                    className="w-24 sm:w-28 h-full object-cover rounded-md flex-shrink-0 group-hover:opacity-90 transition"
                  />
                  <div className="flex flex-col justify-center">
                    <h4 className="text-sm font-semibold text-white mb-1 group-hover:text-gray-300">
                      {article.title}
                    </h4>
                    <p className="text-gray-400 text-xs line-clamp-3">
                      {article.summary}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>


      
      {/* NEWSLETTER SECTION */}
      <section className="bg-white py-8 border-t border-gray-200">
        <div className="w-full px-4 sm:px-4 lg:px-12 text-center">
          <h2 className="text-3xl font-serif font-bold text-gray-900 mb-4">
            Stay Updated with Just Urbane
          </h2>
          <p className="text-xl text-gray-600 mb-8">
            Get the latest in luxury lifestyle, fashion, and culture delivered to your inbox
          </p>
          <div className="flex max-w-md mx-auto">
            <input 
              type="email"
              placeholder="Enter your email address"
              // className="flex-1 px-4 py-3 border border-gray-300 rounded-l-lg focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent"
              className="w-58 sm:w-full px-3 py-2 sm:px-4 sm:py-3 border border-gray-300 rounded-lg sm:rounded-l-lg 
                   focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent text-sm sm:text-base"
            />
            <button className="bg-gray-900 text-white px-4 py-3 rounded-r-lg hover:bg-gray-800 transition-colors font-semibold">
              Subscribe
            </button>
          </div>
        </div>
      </section>

    </div>
  );
};

export default HomePage;
